import path from "path";
import fs from "fs";
import os from "os";
import { RepairRule } from "../repairEngine.js";
import { findPackageJson, directoryExists } from "../../utils/files.js";
import { runCommand } from "../../utils/exec.js";

export const gradleRepairRules: RepairRule[] = [
  {
    id: "gradle-wrapper-validation",
    title: "Validate Android Gradle Wrapper",
    description: "Checks if gradlew or gradle-wrapper.properties files are missing, and regenerates them if Gradle is installed.",
    safe: true,
    async detect(cwd: string): Promise<boolean> {
      const pPath = findPackageJson(cwd);
      if (!pPath) return false;
      const root = path.dirname(pPath);
      const androidPath = path.join(root, "android");
      if (!directoryExists(androidPath)) return false;

      const gradlewName = os.platform() === "win32" ? "gradlew.bat" : "gradlew";
      const gradlewPath = path.join(androidPath, gradlewName);
      const wrapperPropsPath = path.join(androidPath, "gradle", "wrapper", "gradle-wrapper.properties");

      return !fs.existsSync(gradlewPath) || !fs.existsSync(wrapperPropsPath);
    },
    async repair(cwd: string): Promise<{ success: boolean; details?: string }> {
      const pPath = findPackageJson(cwd);
      if (!pPath) return { success: false, details: "package.json not found." };
      const root = path.dirname(pPath);
      const androidPath = path.join(root, "android");

      const gradleCheck = await runCommand("gradle --version");
      if (gradleCheck.error) {
        return {
          success: false,
          details: "Gradle CLI tool is not in PATH. Please install Gradle or download wrapper scripts manually.",
        };
      }

      const result = await runCommand(`cd "${androidPath}" && gradle wrapper`);
      if (result.error) {
        return {
          success: false,
          details: `Failed to generate wrapper: ${result.stderr || result.stdout}`,
        };
      }

      return { success: true, details: "Regenerated Gradle wrapper files successfully." };
    },
  },
  {
    id: "android-build-clean",
    title: "Clean Android Gradle Build Artifacts",
    description: "Cleans Android build artifacts by running './gradlew clean' in the android folder.",
    safe: false,
    async detect(cwd: string): Promise<boolean> {
      const pPath = findPackageJson(cwd);
      if (!pPath) return false;
      const root = path.dirname(pPath);
      const androidPath = path.join(root, "android");
      if (!directoryExists(androidPath)) return false;

      const appBuild = path.join(androidPath, "app", "build");
      const rootBuild = path.join(androidPath, "build");
      return directoryExists(appBuild) || directoryExists(rootBuild);
    },
    async repair(cwd: string): Promise<{ success: boolean; details?: string }> {
      const pPath = findPackageJson(cwd);
      if (!pPath) return { success: false, details: "package.json not found." };
      const root = path.dirname(pPath);
      const androidPath = path.join(root, "android");

      const gradlewName = os.platform() === "win32" ? "gradlew.bat" : "./gradlew";
      const gradlewPath = path.join(androidPath, os.platform() === "win32" ? "gradlew.bat" : "gradlew");

      if (!fs.existsSync(gradlewPath)) {
        return { success: false, details: "gradlew wrapper script is missing. Run wrapper repair first." };
      }

      if (os.platform() !== "win32") {
        // Ensure execution permissions
        await runCommand(`chmod +x "${gradlewPath}"`);
      }

      const result = await runCommand(`cd "${androidPath}" && ${gradlewName} clean`);
      if (result.error) {
        return {
          success: false,
          details: `Gradle clean failed: ${result.stderr || result.stdout}`,
        };
      }

      return { success: true, details: "Gradle build cleaned successfully." };
    },
  },
];
